import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

// Confetti burst that fires on the "paid" screen. Called from the success branch.
function fireConfetti() {
  const duration = 2500;
  const end = Date.now() + duration;
  const colors = ["#2E4FB8", "#D68A3D", "#00d64f", "#ffffff", "#f6c445"];
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 60,
      startVelocity: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 60,
      startVelocity: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // One big center burst
  confetti({
    particleCount: 120,
    spread: 90,
    startVelocity: 42,
    origin: { y: 0.5 },
    colors,
  });
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { branding } = useBranding();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false });
  const [tries, setTries] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [confettiFired, setConfettiFired] = useState(false);

  useEffect(() => {
    if (state.paid && !confettiFired) {
      fireConfetti();
      setConfettiFired(true);
    }
  }, [state.paid, confettiFired]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          const fresh = await refresh();
          setState({ loading: false, paid: true, plan: r.data.plan, isInstructor: fresh?.role === "admin" });
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) { if (!cancelled) setNotFound(true); return; }
      }
      if (!cancelled) {
        setTries(t => t + 1);
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const timedOut = tries > 30; // ~1 min

  return (
    <div className="min-h-screen bg-background paper-texture grid place-items-center p-6">
      <div className="max-w-md w-full text-center bg-card border border-border rounded-md p-10" data-testid="payment-success-page">
        {state.paid ? (
          <>
            <CheckCircle2 size={56} className="mx-auto text-primary mb-4" />
            <div className="label-caps mb-2">You're in</div>
            <h1 className="font-serif text-4xl mb-3">Welcome to {branding.brand_name}.</h1>
            <p className="text-muted-foreground mb-8">
              {state.plan === "whitelabel"
                ? "Your white-label license is unlocked. Next step: tell us how you want your platform branded."
                : state.isInstructor
                ? "Your instructor account is unlocked. Head to the admin dashboard to set up your school."
                : `Your ${state.plan || "course"} access is unlocked. Time to open chapter 1.`}
            </p>
            <Button
              onClick={() =>
                nav(
                  state.plan === "whitelabel"
                    ? "/white-label/onboarding"
                    : state.isInstructor
                    ? "/admin"
                    : "/dashboard",
                )
              }
              className="w-full"
              data-testid="go-dashboard-btn"
            >
              {state.plan === "whitelabel"
                ? "Set up your white-label instance"
                : state.isInstructor
                ? "Go to admin dashboard"
                : "Go to my dashboard"}
            </Button>
          </>
        ) : notFound ? (
          <>
            <XCircle size={56} className="mx-auto text-destructive mb-4" />
            <h1 className="font-serif text-3xl mb-3">Session not found</h1>
            <p className="text-muted-foreground mb-6">This checkout link isn't valid. If you just paid, wait a few seconds and refresh — otherwise pick a plan below.</p>
            <Button onClick={() => nav("/pricing")} className="w-full" data-testid="notfound-back-pricing">Back to plans</Button>
          </>
        ) : timedOut ? (
          <>
            <XCircle size={56} className="mx-auto text-destructive mb-4" />
            <h1 className="font-serif text-3xl mb-3">Still processing…</h1>
            <p className="text-muted-foreground mb-6">Your payment may still be settling. Refresh in a moment or check your dashboard.</p>
            <Button onClick={() => nav("/dashboard")} className="w-full" data-testid="fallback-dashboard-btn">Continue anyway</Button>
          </>
        ) : (
          <>
            <Loader2 size={56} className="mx-auto text-primary mb-4 animate-spin" />
            <h1 className="font-serif text-3xl mb-3">Confirming your payment…</h1>
            <p className="text-muted-foreground">Hang tight — Stripe is telling us the good news.</p>
          </>
        )}
        <div className="mt-8">
          <Link to="/pricing" className="text-xs underline text-muted-foreground">Back to plans</Link>
        </div>
      </div>
    </div>
  );
}
